import { useEffect, useRef, useState } from 'react';
import { likeEscape } from '../lib/format';
import { supabase } from '../lib/supabase';

export interface PickedCustomer { id: string; name: string; min_markup: number | null }

export default function CustomerPicker({ value, onPick }: {
  value: PickedCustomer | null;
  onPick: (c: PickedCustomer | null) => void;
}) {
  const [text, setText] = useState(value?.name ?? '');
  const [open, setOpen] = useState(false);
  const [hits, setHits] = useState<PickedCustomer[]>([]);
  const [hi, setHi] = useState(0);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => setText(value?.name ?? ''), [value?.id]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      const s = text.trim();
      let q = supabase.from('customers').select('id, name, min_markup').eq('archived', false).order('name').limit(12);
      if (s) q = q.ilike('name', `%${likeEscape(s)}%`);
      const { data } = await q;
      setHits((data as PickedCustomer[]) ?? []);
      setHi(0);
    }, 150);
    return () => clearTimeout(t);
  }, [text, open]);

  useEffect(() => {
    const h = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const pick = (c: PickedCustomer) => { onPick(c); setText(c.name); setOpen(false); };

  return (
    <div className="picker" ref={box}>
      <input placeholder="Search customers…" value={text}
        onFocus={() => setOpen(true)}
        onChange={(e) => { setText(e.target.value); setOpen(true); if (!e.target.value) onPick(null); }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') { setHi((h) => Math.min(h + 1, hits.length - 1)); e.preventDefault(); }
          if (e.key === 'ArrowUp') { setHi((h) => Math.max(h - 1, 0)); e.preventDefault(); }
          if (e.key === 'Enter' && hits[hi]) { pick(hits[hi]); e.preventDefault(); }
          if (e.key === 'Escape') setOpen(false);
        }} />
      {open && hits.length > 0 && (
        <div className="picker-list">
          {hits.map((c, i) => (
            <button key={c.id} type="button" className={i === hi ? 'hi' : ''} onMouseDown={(e) => { e.preventDefault(); pick(c); }}>
              {c.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
